<template>
  <div class="drug-report-container">
    <div class="report-card">
      <!-- 标题栏 -->
      <div class="card-header">
        <div class="header-title">
          <i class="el-icon-first-aid-kit"></i>
          <span>药品毛利表</span>
        </div>
      </div>

      <!-- 查询条件 -->
      <div class="query-form">
        <el-form :model="queryParams" size="small" label-width="80px" inline>
          <el-form-item label="药品类型">
            <el-select
                v-model="queryParams.type"
                placeholder="请选择药品类型"
                clearable
                @change="handleSearch"
                style="width: 130px"
            >
              <el-option label="全部" :value="null" />
              <el-option label="西药" :value="1" />
              <el-option label="中药" :value="2" />
              <el-option label="卫材" :value="3" />
            </el-select>
          </el-form-item>
          <el-form-item label="记录筛选">
            <el-select
                v-model="queryParams.isHave"
                placeholder="请选择"
                clearable
                @change="handleSearch"
                style="width: 130px"
            >
              <el-option label="所有药品" :value="null" />
              <el-option label="有记录药品" :value="1" />
            </el-select>
          </el-form-item>
          <el-form-item label="统计时间">
            <el-date-picker
                v-model="timeRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd"
                :default-time="['00:00:00', '23:59:59']"
                @change="handleTimeChange"
                :picker-options="pickerOptions"
            />
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="handleSearch" icon="el-icon-search">查询</el-button>
            <el-button @click="handleReset" icon="el-icon-refresh-right">重置</el-button>
            <el-button type="success" @click="handleExport" icon="el-icon-download">导出</el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 统计卡片区域 -->
      <div class="statistics-cards">
        <div class="stat-card">
          <div class="stat-card-icon sale-icon">
            <i class="el-icon-sold-out"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-label">销售总额</div>
            <div class="stat-value sale-value">
              {{ formatAmount(summaryData.saleAmtSum) }}
              <span class="unit">元</span>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-icon profit-icon">
            <i class="el-icon-s-marketing"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-label">毛利总额</div>
            <div class="stat-value profit-value">
              {{ formatAmount(summaryData.profitAmt) }}
              <span class="unit">元</span>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-icon rate-icon">
            <i class="el-icon-pie-chart"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-label">平均毛利率</div>
            <div class="stat-value rate-value">
              {{ formatRate(summaryData.saleAmtSum, summaryData.profitAmt) }}
            </div>
          </div>
        </div>
      </div>

      <!-- 数据表格 -->
      <div class="table-container">
        <el-table
            :data="tableData"
            border
            stripe
            v-loading="loading"
            style="width: 100%"
            @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="55" align="center" />
          <el-table-column prop="drugName" label="药品名称" min-width="180" align="center" />
          <!-- 新增：药品类型（跟在名称后面） -->
          <el-table-column prop="drugType" label="药品类型" width="100" align="center">
            <template #default="{ row }">
              <el-tag
                  size="mini"
                  :type="getDrugTypeTagType(row.drugType)"
                  v-if="getDrugTypeName(row.drugType)"
              >
                {{ getDrugTypeName(row.drugType) }}
              </el-tag>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="drugSpec" label="药品规格" min-width="150" align="center" />

          <!-- 销售统计 -->
          <el-table-column label="销售统计" align="center">
            <el-table-column prop="xsNumSum" label="销售数量" min-width="100" align="center">
              <template #default="{ row }">
                {{ formatNumber(row.xsNumSum) }}
              </template>
            </el-table-column>
            <el-table-column prop="saleAmtSum" label="销售金额" min-width="120" align="right">
              <template #default="{ row }">
                {{ formatAmount(row.saleAmtSum) }}
              </template>
            </el-table-column>
            <el-table-column prop="saleRatio" label="销售占比" min-width="100" align="center">
              <template #default="{ row }">
                {{ formatPercent(row.saleRatio) }}
              </template>
            </el-table-column>
          </el-table-column>

          <!-- 成本统计 -->
          <el-table-column label="成本统计" align="center">
            <el-table-column prop="purcAmtSum" label="成本金额" min-width="120" align="right">
              <template #default="{ row }">
                {{ formatAmount(row.purcAmtSum) }}
              </template>
            </el-table-column>
            <el-table-column prop="profitAmt" label="毛利金额" min-width="120" align="right">
              <template #default="{ row }">
                {{ formatAmount(row.profitAmt) }}
              </template>
            </el-table-column>
            <el-table-column prop="pricRatio" label="毛利率" min-width="100" align="center">
              <template #default="{ row }">
                {{ formatPercent(row.pricRatio) }}
              </template>
            </el-table-column>
          </el-table-column>

          <!-- 库存统计 -->
          <el-table-column label="库存统计" align="center">
            <el-table-column prop="afrPurcAmtSum" label="库存成本" min-width="100" align="center">
              <template #default="{ row }">
                {{ formatNumber(row.afrPurcAmtSum) }}
              </template>
            </el-table-column>

            <el-table-column prop="agoCntSum" label="期初库存" min-width="100" align="center">
              <template #default="{ row }">
                {{ formatNumber(row.agoCntSum) }}
              </template>
            </el-table-column>
            <el-table-column prop="rkNumSum" label="入库数量" min-width="100" align="center">
              <template #default="{ row }">
                {{ formatNumber(row.rkNumSum) }}
              </template>
            </el-table-column>
            <el-table-column prop="afrCntSum" label="期末库存" min-width="100" align="center">
              <template #default="{ row }">
                {{ formatNumber(row.afrCntSum) }}
              </template>
            </el-table-column>
            <el-table-column prop="reportLoopDays" label="库存周期(天)" min-width="120" align="center">
              <template #default="{ row }">
                {{ row.reportLoopDays || 0 }}
              </template>
            </el-table-column>
          </el-table-column>

          <!-- 价格信息 -->
          <el-table-column label="价格信息" align="center">
            <el-table-column prop="avgPurcPric" label="销售成本单价" min-width="120" align="right">
              <template #default="{ row }">
                {{ formatAmount(row.avgPurcPric) }}
              </template>
            </el-table-column>
            <el-table-column prop="avgSalePric" label="单位销售价" min-width="120" align="right">
              <template #default="{ row }">
                {{ formatAmount(row.avgSalePric) }}
              </template>
            </el-table-column>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="pageNum"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pageSize"
              :total="totalSize"
              layout="total, sizes, prev, pager, next, jumper"
              background
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { drugStatistics } from "@/api/drugStatistics/drugStatistics.js";
import dayjs from "dayjs";

export default {
  name: "DrugReport",
  data() {
    return {
      loading: false,
      // 时间范围（默认当月，只存年月日用于页面显示）
      timeRange: [
        dayjs().startOf('month').format('YYYY-MM-DD'),
        dayjs().endOf('month').format('YYYY-MM-DD')
      ],
      // 查询参数
      queryParams: {
        type: null,  // 默认西药：1西药 2中药 3卫材，null查询所有
        startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
        isHave: 1,
      },
      // 分页参数
      pageNum: 1,
      pageSize: 10,
      totalSize: 0,
      // 表格数据
      tableData: [],
      // 选中的行
      selectedRows: [],
      // 药品类型字典
      drugTypeMap: {
        1: { name: '西药', tagType: '' },
        2: { name: '中药', tagType: 'success' },
        3: { name: '卫材', tagType: 'warning' }
      },
      // 时间选择器快捷选项
      pickerOptions: {
        shortcuts: [
          {
            text: '本月',
            onClick(picker) {
              const start = dayjs().startOf('month').format('YYYY-MM-DD');
              const end = dayjs().endOf('month').format('YYYY-MM-DD');
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '上月',
            onClick(picker) {
              const start = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
              const end = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '本季度',
            onClick(picker) {
              const start = dayjs().startOf('quarter').format('YYYY-MM-DD');
              const end = dayjs().endOf('quarter').format('YYYY-MM-DD');
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '本年',
            onClick(picker) {
              const start = dayjs().startOf('year').format('YYYY-MM-DD');
              const end = dayjs().endOf('year').format('YYYY-MM-DD');
              picker.$emit('pick', [start, end]);
            }
          }
        ]
      }
    };
  },
  computed: {
    // 汇总数据 —— 从返回数据的第一个对象中取总计字段
    summaryData() {
      if (this.tableData.length > 0) {
        const first = this.tableData[0];
        return {
          saleAmtSum: first.allSaleAmtSum || 0,
          profitAmt: first.allProfitAmt || 0,
          // 以下字段虽然不再使用，但保留以兼容可能的模板引用
          afrCntSum: first.afrCntSum || 0
        };
      }
      return { saleAmtSum: 0, profitAmt: 0, afrCntSum: 0 };
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    // 将日期转换为带时分秒的格式
    formatDateWithTime(dateStr, isEndOfDay = false) {
      if (!dateStr) return '';
      if (isEndOfDay) {
        return dayjs(dateStr).endOf('day').format('YYYY-MM-DD HH:mm:ss');
      }
      return dayjs(dateStr).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    },

    // 药品类型名称
    getDrugTypeName(type) {
      const item = this.drugTypeMap[type];
      return item ? item.name : '';
    },

    // 药品类型标签样式
    getDrugTypeTagType(type) {
      const item = this.drugTypeMap[type];
      return item ? item.tagType : '';
    },

    // 加载数据
    async loadData() {
      this.loading = true;
      try {
        // 将页面选择的年月日转换为带时分秒的格式
        const startDate = this.formatDateWithTime(this.queryParams.startDate, false);
        const endDate = this.formatDateWithTime(this.queryParams.endDate, true);

        // type：1西药 2中药 3卫材，其他（null/''/undefined）查询所有
        const type =
            this.queryParams.type === '' ||
            this.queryParams.type === undefined ||
            this.queryParams.type === null
                ? null
                : this.queryParams.type;

        // isHave：1查询有记录药品报表，其他查询所有药品报表
        const isHave =
            this.queryParams.isHave === '' ||
            this.queryParams.isHave === undefined ||
            this.queryParams.isHave === null
                ? null
                : this.queryParams.isHave;

        const params = {
          type: type,
          startDate: startDate,
          endDate: endDate,
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          isHave: isHave,
        };

        console.log('请求参数:', params);
        const res = await drugStatistics(params);
        if (res.code === 200) {
          this.tableData = (res.data && res.data.content) || [];
          this.totalSize = (res.data && res.data.totalSize) || 0;
        } else {
          this.$message.error(res.message || '获取数据失败');
        }
      } catch (error) {
        console.error('加载药品统计数据失败:', error);
        this.$message.error('系统异常，请联系管理员');
      } finally {
        this.loading = false;
      }
    },

    // 搜索
    handleSearch() {
      this.pageNum = 1;
      this.loadData();
    },

    // 重置
    handleReset() {
      this.queryParams = {
        type: 1,  // 重置为西药
        startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
        isHave: null,
      };
      this.timeRange = [
        dayjs().startOf('month').format('YYYY-MM-DD'),
        dayjs().endOf('month').format('YYYY-MM-DD')
      ];
      this.pageNum = 1;
      this.pageSize = 10;
      this.loadData();
    },

    // 时间变化
    handleTimeChange(val) {
      if (val && val.length === 2) {
        this.queryParams.startDate = val[0];
        this.queryParams.endDate = val[1];
        this.handleSearch();
      } else {
        // 清空时间时，恢复默认当月
        this.queryParams.startDate = dayjs().startOf('month').format('YYYY-MM-DD');
        this.queryParams.endDate = dayjs().endOf('month').format('YYYY-MM-DD');
        this.handleSearch();
      }
    },

    // 分页大小变化
    handleSizeChange(val) {
      this.pageSize = val;
      this.pageNum = 1;
      this.loadData();
    },

    // 当前页变化
    handleCurrentChange(val) {
      this.pageNum = val;
      this.loadData();
    },

    // 表格选中变化
    handleSelectionChange(val) {
      this.selectedRows = val;
    },

    // 格式化金额
    formatAmount(amount) {
      if (amount === null || amount === undefined) return '0.00';
      const num = Number(amount);
      if (isNaN(num)) return '0.00';
      return num.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    // 格式化数字（整数）
    formatNumber(num) {
      if (num === null || num === undefined) return '0.00';
      const value = Number(num);
      if (isNaN(value)) return '0.00';
      return value.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    // 格式化百分比
    formatPercent(percent) {
      if (percent === null || percent === undefined) return '0.00%';
      // 如果已经是百分比格式的字符串
      if (typeof percent === 'string' && percent.includes('%')) {
        return percent;
      }
      const num = Number(percent);
      if (isNaN(num)) return '0.00%';
      return num.toFixed(2) + '%';
    },

    // 格式化毛利率（销售金额和毛利计算）
    formatRate(saleAmount, profitAmount) {
      const sale = Number(saleAmount) || 0;
      const profit = Number(profitAmount) || 0;
      if (sale === 0) return '0.00%';
      const rate = (profit / sale) * 100;
      return rate.toFixed(2) + '%';
    },

    // 导出报表
    handleExport() {
      if (this.tableData.length === 0) {
        this.$message.warning('暂无数据可导出');
        return;
      }

      // 构建导出数据
      const exportData = this.tableData.map(row => ({
        '药品名称': row.drugName || '',
        '药品类型': this.getDrugTypeName(row.drugType) || '',
        '药品规格': row.drugSpec || '',
        '销售数量': this.formatNumber(row.xsNumSum),
        '销售金额': this.formatAmount(row.saleAmtSum),
        '销售占比': this.formatPercent(row.saleRatio),
        '成本金额': this.formatAmount(row.purcAmtSum),
        '毛利金额': this.formatAmount(row.profitAmt),
        '毛利率': this.formatPercent(row.pricRatio),
        '期初库存': this.formatNumber(row.agoCntSum),
        '入库数量': this.formatNumber(row.rkNumSum),
        '期末库存': this.formatNumber(row.afrCntSum),
        '库存周期(天)': row.reportLoopDays || 0,
        '销售成本单价': this.formatAmount(row.avgPurcPric),
        '单位销售价': this.formatAmount(row.avgSalePric)
      }));

      // 添加合计行（使用总计字段，同样取第一个对象的总计）
      const first = this.tableData[0] || {};
      const totalSale = first.allSaleAmtSum || 0;
      const totalProfit = first.allProfitAmt || 0;
      // 以下明细合计用于展示，但实际导出合计行我们直接用总计字段，更准确
      exportData.push({
        '药品名称': '合计',
        '药品类型': '',
        '药品规格': '',
        '销售数量': this.formatNumber(this.tableData.reduce((sum, row) => sum + (row.xsNumSum || 0), 0)),
        '销售金额': this.formatAmount(totalSale),
        '销售占比': '',
        '成本金额': this.formatAmount(this.tableData.reduce((sum, row) => sum + (row.purcAmtSum || 0), 0)),
        '毛利金额': this.formatAmount(totalProfit),
        '毛利率': this.formatRate(totalSale, totalProfit),
        '期初库存': this.formatNumber(this.tableData.reduce((sum, row) => sum + (row.agoCntSum || 0), 0)),
        '入库数量': this.formatNumber(this.tableData.reduce((sum, row) => sum + (row.rkNumSum || 0), 0)),
        '期末库存': this.formatNumber(this.tableData.reduce((sum, row) => sum + (row.afrCntSum || 0), 0)),
        '库存周期(天)': '',
        '销售成本单价': '',
        '单位销售价': ''
      });

      // 转换为 CSV
      const headers = Object.keys(exportData[0]);
      const csvRows = [
        headers.join(','),
        ...exportData.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ];
      const csvContent = csvRows.join('\n');

      // 下载文件
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `药品统计报表_${dayjs().format('YYYYMMDDHHmmss')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.$message.success('导出成功');
    }
  }
};
</script>

<style scoped lang="less">
.drug-report-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100%;

  .report-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    .card-header {
      padding: 16px 24px;
      background: linear-gradient(135deg, #67c23a 0%, #409eff 100%);
      color: #fff;

      .header-title {
        display: flex;
        align-items: center;
        font-size: 18px;
        font-weight: 600;

        i {
          font-size: 24px;
          margin-right: 10px;
        }
      }
    }

    .query-form {
      padding: 16px 24px;
      border-bottom: 1px solid #ebeef5;
      background: #fafbfc;
    }

    .statistics-cards {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
      padding: 24px;

      .stat-card {
        display: flex;
        align-items: center;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
        transition: all 0.3s ease;

        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .stat-card-icon {
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          margin-right: 16px;
          font-size: 28px;

          &.sale-icon {
            background: linear-gradient(135deg, #e6f7ff, #bae7ff);
            color: #409EFF;
          }

          &.profit-icon {
            background: linear-gradient(135deg, #f6ffed, #d9f7be);
            color: #67C23A;
          }

          &.rate-icon {
            background: linear-gradient(135deg, #f9f0ff, #efdbff);
            color: #722ED1;
          }

          &.inventory-icon {
            background: linear-gradient(135deg, #fff7e6, #ffe7ba);
            color: #E6A23C;
          }
        }

        .stat-card-content {
          flex: 1;

          .stat-label {
            font-size: 14px;
            color: #909399;
            margin-bottom: 8px;
          }

          .stat-value {
            font-size: 24px;
            font-weight: bold;

            .unit {
              font-size: 12px;
              font-weight: normal;
              margin-left: 4px;
              color: #909399;
            }

            &.sale-value {
              color: #409EFF;
            }

            &.profit-value {
              color: #67C23A;
            }

            &.rate-value {
              color: #722ED1;
              font-size: 20px;
            }

            &.inventory-value {
              color: #E6A23C;
            }
          }
        }
      }
    }

    .table-container {
      padding: 0 24px 24px 24px;

      .pagination-container {
        margin-top: 16px;
        display: flex;
        justify-content: flex-end;
      }
    }
  }
}

// 响应式布局
@media (max-width: 1200px) {
  .drug-report-container .report-card .statistics-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .drug-report-container {
    padding: 12px;

    .report-card {
      .card-header {
        padding: 12px 16px;
      }

      .query-form {
        padding: 12px 16px;

        :deep(.el-form-item) {
          display: block;
          margin-bottom: 12px;
        }
      }

      .statistics-cards {
        grid-template-columns: 1fr;
        gap: 12px;
        padding: 16px;
      }

      .table-container {
        padding: 0 16px 16px 16px;
        overflow-x: auto;
      }
    }
  }
}
</style>